import { assetNameFluid, assetTooltipFluid, assetErrorFluid } from './fluidStrings.locstring.json';
import { assetNameTable, assetTooltipTable, assetErrorTable } from './fluidTable.locstring.json';
import { ControlIcons } from 'owa-control-icons';
import type { FluidPayload } from './schema';
import { isFluidEnabledForSource, FluidOwaSource } from 'owa-fluid-validations';
import { LazyModule, LazyModuleType, LazyAction, createLazyComponent } from 'owa-bundling';
import { Loading, NetworkError } from 'owa-timestream-loadingpanel';
import { AssetAriaLabel, registerAsset, DEFAULT_MIN_ASSET_WIDTH } from 'owa-timestream-asset-sdk';
import createFluidAssetFromClipboardData from './util/createFluidAssetFromClipboardData';
import getDocumentName from './util/getDocumentName';
import loc from 'owa-localize';
import * as styles from './viewStyle.scss';

const lazyModule = new LazyModule(
    () => import(/* webpackChunkName: "CalendarBoardFluidAsset" */ './lazyIndex')
);

const LazyFluidView = createLazyComponent<
    { payload: FluidPayload },
    LazyModuleType<typeof lazyModule>,
    {}
>(lazyModule, m => m.FluidView, Loading, NetworkError);

const lazyCreateNewFluidAsset = new LazyAction(lazyModule, m => m.createNewFluidAsset);
const lazyCreateNewFluidTableAsset = new LazyAction(lazyModule, m => m.createNewFluidTableAsset);

function createNewFluidAsset(pageX: number, pageY: number) {
    return lazyCreateNewFluidAsset.importAndExecute(pageX, pageY);
}

function createNewFluidTableAsset(pageX: number, pageY: number) {
    return lazyCreateNewFluidTableAsset.importAndExecute(pageX, pageY);
}

function getSearchString(payload: FluidPayload) {
    return loc(assetNameFluid) + (payload.url ? ' - ' + getDocumentName(payload.url) : '');
}

function getAriaLabel(payload: FluidPayload, assetNameResourceId: string) {
    return loc(
        AssetAriaLabel.assetAriaLabel,
        loc(assetNameResourceId),
        payload.url ? getDocumentName(payload.url) : ''
    );
}

const FLUID_TYPE = 'fluid';
const TABLE_TYPE = 'table';

export default registerAsset<FluidPayload>({
    type: FLUID_TYPE,
    iconName: ControlIcons.FluidLogo,
    getToolName: () => loc(assetNameFluid),
    getTooltip: () => loc(assetTooltipFluid),
    getErrorMessage: () => loc(assetErrorFluid),
    isAssetEnabled: () => isFluidEnabledForSource(FluidOwaSource.Timestream),
    unsupportedDefaultCommands: [],
    viewStyle: styles.viewStyle,
    CanvasView: LazyFluidView,
    createAssets: createNewFluidAsset,
    createEmptyAsset: () => ({
        type: FLUID_TYPE,
        payload: { url: '' },
    }),
    createAssetFromClipboardData: createFluidAssetFromClipboardData,
    getAriaLabel: (payload: FluidPayload) => getAriaLabel(payload, assetNameFluid),
    getTitleString: getSearchString,
    getSearchString: getSearchString,
    assetResizeConfiguration: () => ({
        minWidth: DEFAULT_MIN_ASSET_WIDTH,
        minHeight: 140,
        defaultWidth: 420,
        defaultHeight: 350,
    }),
});

export const table = registerAsset<FluidPayload>({
    type: TABLE_TYPE,
    iconName: ControlIcons.Table,
    getToolName: () => loc(assetNameTable),
    getTooltip: () => loc(assetTooltipTable),
    getErrorMessage: () => loc(assetErrorTable),
    isAssetEnabled: () => isFluidEnabledForSource(FluidOwaSource.Timestream),
    unsupportedDefaultCommands: [],
    viewStyle: styles.viewStyle,
    CanvasView: LazyFluidView,
    createAssets: createNewFluidTableAsset,
    createEmptyAsset: () => ({
        type: TABLE_TYPE,
        payload: { url: '' },
    }),
    getAriaLabel: (payload: FluidPayload) => getAriaLabel(payload, assetNameTable),
    getTitleString: getSearchString,
    getSearchString: getSearchString,
    assetResizeConfiguration: () => ({
        minWidth: DEFAULT_MIN_ASSET_WIDTH,
        minHeight: 140,
        defaultWidth: 770,
        defaultHeight: 300,
    }),
});
